/*
 * Retryable sign-in policy for requests whose credentials expire while the page is open.
 * Used by the API authentication flow and the recovery dialog presenter.
 * Keeps one shared attempt so concurrent failures do not open competing sign-in prompts.
 * Example: const recovery = createAuthRecovery(context);
 * Install its API hooks with installAuthRecovery({ context, ...options }).
 */

import { AUTH_CANCELLED_MESSAGE } from '../../runtime/auth.js';
import { servicesContext } from '../../runtime/services.js';
import { createConsent } from './coc-modal.js';

export const recoveryCancelled = () =>
  Object.assign(new Error(AUTH_CANCELLED_MESSAGE), { code: 'auth_cancelled' });

export function createAuthRecovery(context) {
  context = servicesContext(context, ['api', 'auth']);
  const { auth } = context.services;
  const browser = context.window;
  let presenter, current;

  async function credentialRequest(path, token, body, signal) {
    const response = await context.fetch(path, {
      method: body === undefined ? 'GET' : 'POST',
      signal,
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' })
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
    const data = response.status === 204 ? null : await response.json();
    if (!response.ok) throw new Error(data?.error?.message || 'Could not restore your session.');
    return data;
  }

  function attach(present) {
    if (presenter && presenter !== present)
      throw new Error('This root already owns an authentication presenter.');
    presenter = present;
    if (current) display(current);
    return () => {
      if (presenter === present) {
        current?.finish();
        presenter = null;
      }
    };
  }

  function display(request) {
    if (!presenter) return;
    try {
      Promise.resolve(presenter(request)).catch(() => request.finish());
    } catch (error) {
      request.finish();
      throw error;
    }
  }

  function install({ anonymous, resume, cancel, home }) {
    auth.configureAuthRecovery({
      anonymous,
      resume,
      open(attempt) {
        const signal = AbortSignal.any([attempt.controller.signal, context.signal]);
        const request = {
          attempt,
          signal,
          completed: false,
          publicLogin: attempt.intent === 'public-login',
          navigate(path) {
            request.finish();
            browser.navigate(path);
          },
          leave() {
            if (attempt.required) {
              auth.cancelAuthRecovery();
              home();
            } else request.finish();
          },
          finish() {
            if (current !== request) return;
            current = null;
            signal.removeEventListener('abort', request.finish);
            try {
              presenter?.(null);
            } finally {
              if (!request.completed && !signal.aborted) {
                auth.cancelAuthRecovery();
                cancel?.(attempt);
              }
            }
          },
          async authenticate(data, gateConsent) {
            auth.assertAuthRecovery(attempt);
            if (!data?.token || !data.user?.id) throw new Error('Invalid sign-in response.');
            if (attempt.user && data.user.id !== attempt.user.id) {
              await credentialRequest('/api/auth/logout', data.token, {}, signal);
              throw new Error(
                `Sign in as ${attempt.user.username || 'the original account'} to continue.`
              );
            }
            if (data.user.status !== 'active' && browser.location.pathname !== '/me')
              throw new Error('Verify your email, then sign in again to continue.');
            const doc = await credentialRequest(
              '/api/code-of-conduct',
              data.token,
              undefined,
              signal
            );
            auth.assertAuthRecovery(attempt);
            if (
              doc?.version > (data.user.accepted_coc_version || 0) &&
              data.user.status === 'active'
            )
              await gateConsent({ doc, data });
            auth.assertAuthRecovery(attempt);
            await auth.resumeAuthRecovery(attempt, data, () => {
              request.completed = true;
              request.finish();
              context.service(createConsent).dismissAcceptedCodeOfConduct();
            });
          },
          async accept(consent) {
            const user = await credentialRequest(
              '/api/me/accept-code-of-conduct',
              consent.data.token,
              { version: consent.doc.version },
              signal
            );
            auth.assertAuthRecovery(attempt);
            consent.data.user.accepted_coc_version = user.accepted_coc_version;
          }
        };
        current = request;
        signal.addEventListener('abort', request.finish, { once: true });
        if (signal.aborted) request.finish();
        else display(request);
      }
    });
  }

  context.own(() => {
    current?.finish();
    auth.configureAuthRecovery(null);
  });
  return { attach, install };
}

export function installAuthRecovery({ context, ...options }) {
  return context.service(createAuthRecovery).install(options);
}
